import { BaseTypography } from '@/components/common/baseText/BaseTypography';
import { getPromotionTool } from '@/components/images/base64/PromotionTool';
import { AudioPlayContext } from '@/contexts/AudioPlayContext';
import { GaContext } from '@/contexts/GaContext';
import { ScreenContext } from '@/contexts/ScreenContext';
import { usePromotionTool } from '@/hooks/popup/promotiontool/usePromotionTool';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { memo, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { WebSocketContext } from '../../../../contexts/WebSocketContext';
import { CMDPsQueryPromotionRanking } from '../../../../models/cmd/live';
import { POPUP_CONFIRM_ACTION, POPUP_TYPE } from '../../../../models/Popup';
import { RootState } from '../../../../store/store';
import { numberFormat } from '../../../../utils/commonFunc';
import {
    getMainUserState,
    getPromotionToolState,
} from '../../../main/selector';
import { promotionToolSliceActions } from '../../../main/slice/promotiontool/promotionTool';
import { getPopupState } from '../../selector';
import { popupSliceActions } from '../../slice';
import { getPromotionTitle, promotionRanking } from '../PromotionObjectMapper';
import {
    dateToString,
    dateToTimeLeftArray,
    getDisplayTimeZone,
    getImageJsonArray,
    ImageType,
    startQuery,
    stopQuery,
    timeToString,
} from '../PromotionUtil';
import { PromotionEventType, PromotionPage } from './PromotionDialog';
import { PromotionRankingCell } from './PromotionRankingCell';
import { PromotionClock } from './PromotionSplash';

enum RankType {
    RTRedeemAmount = 0,
    RTWashAmount,
    RTResultAmount,
}

const amountTitle = {
    [RankType.RTRedeemAmount]: 'promotion_tool.rankings_win_amount',
    [RankType.RTWashAmount]: 'leaderboard.valid_bet_amount',
    [RankType.RTResultAmount]: 'leaderboard.win_loss',
};

type MemoizedRankingListProps = {
    type: PromotionEventType;
    ranking: RankingProps[];
    leaderboard: boolean;
    landscape: boolean;
};

const MemoizedRankingList = memo((props: MemoizedRankingListProps) => (
    <AutoSizer>
        {() => (
            <FixedSizeList
                className="fixed-size-list"
                height={
                    props.leaderboard
                        ? props.landscape
                            ? 528 / 1.2
                            : 470
                        : props.landscape
                          ? 538 / 1.2
                          : 470
                }
                width={props.landscape ? 1300 / 1.2 : 500}
                itemCount={props.ranking.length}
                itemSize={props.leaderboard ? (props.landscape ? 50 : 52) : 52}
                itemData={props}
            >
                {({ index, style, data }) => (
                    <div style={style}>
                        <PromotionRankingCell
                            eventType={data.type}
                            ranking={data.ranking[index]}
                            key={`promotiontool-ranking-${index}`}
                        />
                    </div>
                )}
            </FixedSizeList>
        )}
    </AutoSizer>
));
MemoizedRankingList.displayName = 'MemoizedRankingList';

export type RankingProps = {
    Rank: number;
    PlayerID: number;
    Username: string;
    WinAmount: number;
    LastRank: boolean;
    Self: boolean;
};
export const PromotionRanking = () => {
    const { isLandscape } = useContext(ScreenContext);
    const { playButtonAudio } = useContext(AudioPlayContext);
    const { isLeaderboard, eventEnded } = usePromotionTool();
    const dispatch: Dispatch<AnyAction> = useDispatch();
    const { sendCommand } = useContext(WebSocketContext);
    const {
        EventType,
        RankData,
        RankByType,
        PlayerRank,
        PlayerAmount,
        RankingUTCUpdateTime,
        RankCount,
        Timezone,
        EndTimestamp,
    } = useSelector((state: RootState) => getPromotionToolState(state));
    const confirmFunc = useSelector(getPopupState).confirmValue;
    const { IsTrial, PlayerID } = useSelector((state: RootState) =>
        getMainUserState(state)
    );

    const [ranking, setRanking] = useState<RankingProps[]>([]);
    const { t, i18n } = useTranslation();

    const [showPlayerRank, setShowPlayerRank] = useState(false);
    const [isPlayerFirst3, setPlayerFirst3] = useState(false);
    const [displayPlayerRank, setDisplayPlayerRank] = useState('-');

    const [showPlayerName, setShowPlayerName] = useState(false);
    const [displayPlayerName, setDisplayPlayerName] = useState(t('system.you'));

    const [showPlayerAmount, setShowPlayerAmount] = useState(false);
    const [displayPlayerAmount, setDisplayPlayerAmount] = useState('-');

    const displayTimezone = getDisplayTimeZone(Timezone);
    const [time, setTime] = useState<string[]>(['']);

    const { page } = useContext(GaContext);

    const doQuery = () => {
        dispatch(promotionToolSliceActions.isLoading(true));
        const cmd = new CMDPsQueryPromotionRanking();
        sendCommand(cmd);
        startQuery(onTimeout);

        if (eventEnded()) {
            //get last data when event ended
            updateRankData();
        }
    };

    const onTimeout = () => {
        stopQuery();
        dispatch(promotionToolSliceActions.isLoading(false));
        dispatch(
            popupSliceActions.open(
                'promotion_tool.query_timeout',
                POPUP_TYPE.CONFIRM,
                POPUP_CONFIRM_ACTION.RETRY,
                undefined
            )
        );
    };

    useEffect(() => {
        doQuery();
        if (!isLeaderboard()) return;

        const updateTimer = () =>
            setTime(
                getImageJsonArray(
                    ImageType.Countdown,
                    dateToTimeLeftArray(EndTimestamp, displayTimezone).join(':')
                )
            );
        updateTimer();
        const timer = setInterval(() => {
            if (eventEnded()) {
                clearInterval(timer);
            } else updateTimer();
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        updateRankData();
    }, [RankData, PlayerAmount, PlayerRank, RankingUTCUpdateTime, RankCount]);

    useEffect(() => {
        if (confirmFunc === POPUP_CONFIRM_ACTION.RETRY) {
            doQuery();
        }
    }, [confirmFunc]);

    const updateRankData = () => {
        const isEmptyData = !RankData || RankData.length == 0;
        if (RankData) {
            dispatch(promotionToolSliceActions.isLoading(false));
            stopQuery();
            setShowPlayerName(true);
            setShowPlayerRank(true);
            setShowPlayerAmount(true);
        }

        //Ranking
        let ranking: RankingProps[] = [];
        if (!isEmptyData) {
            //update
            RankData.forEach((str, index) => {
                const data: RankingProps = JSON.parse(str);
                data.LastRank = index === RankData.length - 1;
                data.Self = data.PlayerID === PlayerID;
                ranking.push(data);
            });
        }
        setRanking(ranking);

        //Player
        setPlayerFirst3(PlayerRank > 0 && PlayerRank <= 3);

        if (!isEmptyData) {
            setDisplayPlayerRank(
                getRankString(PlayerRank, PlayerAmount, RankCount, ranking)
            );
            setDisplayPlayerAmount(
                PlayerAmount ? numberFormat(PlayerAmount / 100) : '-'
            );
        }

        // Trial player account handling
        if (IsTrial && !isEmptyData) {
            setDisplayPlayerRank('-');
            setDisplayPlayerName('-');
            setDisplayPlayerAmount('-');
        }
    };

    const getRankString = (
        rank: number,
        winAmount: number | undefined,
        rankCount: number,
        rankData: Array<RankingProps>
    ): string => {
        if (!isLeaderboard()) {
            if (rank > 0) {
                if (winAmount && winAmount > 0) {
                    if (rank <= rankData.length) return rank.toString();
                    else if (rank < rankCount) return `${rankData.length}+`;
                }
            } else if (rank <= 0 && winAmount && winAmount > 0) {
                return `${rankCount}+`;
            }
        } else {
            if (rank > 0 && winAmount) {
                return rank > rankCount ? `${rankCount}+` : `${rank}`;
            }
        }
        return '-';
    };

    const destroy = () => {
        stopQuery();
        dispatch(promotionToolSliceActions.clearRanking());
        // dispatch(promotionToolSliceActions.clearRankCount());
    };

    return (
        <Box sx={{ position: 'relative', height: '100%' }}>
            <Box
                className={`event-title ${!isLeaderboard() ? '' : 'leaderboard'}`}
            >
                <Box>
                    {getPromotionTool(
                        getPromotionTitle(
                            isLeaderboard()
                                ? `event_leaderboard_title_${i18n.language.replace(/-/g, '_')}`
                                : `event_title_${i18n.language.replace(/-/g, '_')}`
                        )
                    )}
                </Box>
                {isLeaderboard() && (
                    <>
                        {!eventEnded() ? (
                            <Box className="leaderboard-title-countdown">
                                <Box className="event-remain-time ranking-page-style">
                                    {time.map((value, index) => (
                                        <PromotionClock
                                            key={`event-remain-time-${index}`}
                                            str={value}
                                            type={EventType}
                                        />
                                    ))}
                                </Box>
                                <Stack
                                    sx={{
                                        position: 'relative',
                                        display: 'flex',
                                        color: '#C6C6C6',
                                    }}
                                    justifyContent={'center'}
                                    direction={'row'}
                                    spacing={0}
                                >
                                    <Typography
                                        className={`time-text ${isLeaderboard() ? 'leaderboard' : ''}`}
                                    >
                                        {t('promotion_tool.days')}
                                    </Typography>
                                    <Typography
                                        className={`time-text ${isLeaderboard() ? 'leaderboard' : ''}`}
                                    >
                                        {t('promotion_tool.hours')}
                                    </Typography>
                                    <Typography
                                        className={`time-text ${isLeaderboard() ? 'leaderboard' : ''}`}
                                    >
                                        {t('promotion_tool.minutes')}
                                    </Typography>
                                    <Typography
                                        className={`time-text ${isLeaderboard() ? 'leaderboard' : ''}`}
                                    >
                                        {t('promotion_tool.seconds')}
                                    </Typography>
                                </Stack>
                            </Box>
                        ) : (
                            <Typography className="leaderboard-title-event-ended">
                                {t('leaderboard.event_ended')}
                            </Typography>
                        )}
                    </>
                )}
            </Box>
            <Box
                className={`event-detail-list-content ${!isLeaderboard() ? '' : 'leaderboard'}`}
            >
                <Box
                    sx={{
                        backgroundColor: '#bea57b',
                        width: '100%',
                        height: '2px',
                    }}
                ></Box>
                <Box
                    sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        width: '100%',
                        height: '40px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Typography
                        sx={{
                            color: '#bf9a5d',
                            textAlign: 'center',
                            fontSize: '18px',
                        }}
                    >
                        {t('promotion_tool.rankings')}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        backgroundColor: '#bea57b',
                        width: '100%',
                        height: '2px',
                    }}
                ></Box>
                <Box
                    className={`ranking-title-container ${isLeaderboard() ? 'leaderboard' : ''}`}
                >
                    <Typography
                        className={`ranking-title-no ${isLeaderboard() ? 'leaderboard' : ''}`}
                    >
                        {t('promotion_tool.rankings_num')}
                    </Typography>
                    <Typography
                        className={`ranking-title-player ${isLeaderboard() ? 'leaderboard' : ''}`}
                    >
                        {t('promotion_tool.rankings_player')}
                    </Typography>
                    <Typography
                        className={`ranking-title-win-amount ${isLeaderboard() ? 'leaderboard' : ''}`}
                    >
                        {/* {`${t('promotion_tool.rankings_win_amount')} ${'(' + currency + ')'}`} */}
                        {`${t(isLeaderboard() ? amountTitle[RankByType as RankType] : 'promotion_tool.rankings_win_amount')}`}
                    </Typography>
                </Box>
                <div
                    className={`ranking-list ${isLeaderboard() ? 'leaderboard' : ''}`}
                >
                    <MemoizedRankingList
                        type={EventType}
                        ranking={ranking}
                        leaderboard={isLeaderboard()}
                        landscape={isLandscape}
                    />
                </div>
                <Box
                    className={`ranking-current-player ${isLeaderboard() ? 'leaderboard' : ''}`}
                >
                    <Box
                        className={`ranking-item-rank ${isLeaderboard() ? 'leaderboard' : ''}`}
                    >
                        {showPlayerRank ? (
                            isPlayerFirst3 && PlayerRank <= RankCount ? (
                                <Box>
                                    {getPromotionTool(
                                        promotionRanking[
                                            isLeaderboard()
                                                ? `event_leaderboard_no_${PlayerRank}`
                                                : `event_no_${PlayerRank}`
                                        ]
                                    )}
                                </Box>
                            ) : (
                                <Typography
                                    sx={{
                                        minWidth: '48px',
                                        minHeight: '48px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        fontSize: '20px',
                                    }}
                                >
                                    {displayPlayerRank}
                                </Typography>
                            )
                        ) : null}
                    </Box>
                    {showPlayerName ? (
                        <Typography
                            className={`ranking-item-player ${isLeaderboard() ? 'leaderboard' : ''}`}
                        >
                            {displayPlayerName}
                        </Typography>
                    ) : (
                        <></>
                    )}
                    {showPlayerAmount ? (
                        <Typography
                            className={`ranking-item-amount ${isLeaderboard() ? 'leaderboard' : ''}`}
                        >
                            {displayPlayerAmount}
                        </Typography>
                    ) : (
                        <></>
                    )}
                </Box>
                <Stack
                    className="ranking-last-update"
                    direction={'row'}
                    spacing={0.5}
                >
                    <Typography
                        sx={{
                            color: '#979797',
                            textAlign: 'right',
                            fontSize: '14px',
                        }}
                    >{`${t('promotion_tool.last_update')}`}</Typography>
                    <Typography
                        sx={{
                            color: '#979797',
                            textAlign: 'right',
                            fontSize: '14px',
                        }}
                    >
                        {RankingUTCUpdateTime > 0
                            ? `${dateToString(new Date((RankingUTCUpdateTime + displayTimezone * 60 * 60) * 1000), false, '-')} ${timeToString(new Date((RankingUTCUpdateTime + displayTimezone * 60 * 60) * 1000), false, ':')} (UTC${displayTimezone >= 0 ? '+' + displayTimezone : displayTimezone})`
                            : `-`}
                    </Typography>
                </Stack>
            </Box>
            <Stack className="page-buttons" direction={'row'} spacing={'20px'}>
                {!isLeaderboard() && (
                    <Button
                        id={`${page}_re_popup_explore-rewards`}
                        className="button-gold"
                        onClick={() => {
                            destroy();
                            playButtonAudio();

                            if (eventEnded()) {
                                dispatch(
                                    promotionToolSliceActions.isOpenDialog(
                                        false
                                    )
                                );
                                dispatch(
                                    popupSliceActions.open(
                                        'promotion_tool.popup_event_ended'
                                    )
                                );
                                return;
                            }

                            dispatch(
                                promotionToolSliceActions.updatePage(
                                    PromotionPage.Gacha
                                )
                            );
                        }}
                    >
                        <BaseTypography
                            resize={{
                                direction: 'horizontal',
                                value: '200px',
                            }}
                            sx={{
                                fontSize: '20px',
                            }}
                        >
                            {t('promotion_tool.explore_rewards')}
                        </BaseTypography>
                    </Button>
                )}
                <Button
                    className="button-black"
                    id={
                        !isLeaderboard()
                            ? `${page}_re_popup_records`
                            : `${page}_popup_back-to-game`
                    }
                    onClick={() => {
                        destroy();
                        playButtonAudio();

                        if (!isLeaderboard()) {
                            //Promotion tool click handle
                            if (eventEnded()) {
                                dispatch(
                                    promotionToolSliceActions.isOpenDialog(
                                        false
                                    )
                                );
                                dispatch(
                                    popupSliceActions.open(
                                        'promotion_tool.popup_event_ended'
                                    )
                                );
                                return;
                            }

                            dispatch(
                                promotionToolSliceActions.updatePage(
                                    PromotionPage.Record
                                )
                            );
                        } else {
                            //Leaderboard click handle
                            dispatch(
                                promotionToolSliceActions.isOpenDialog(false)
                            );
                        }
                    }}
                >
                    {t(
                        !isLeaderboard()
                            ? 'promotion_tool.records'
                            : 'leaderboard.back_to_game'
                    )}
                </Button>
            </Stack>
            {RankData && ranking.length < 1 ? (
                <Typography
                    className={`no-promotion-records ${isLeaderboard() ? 'leaderboard' : ''}`}
                >
                    {t('promotion_tool.no_records')}
                </Typography>
            ) : (
                <></>
            )}
        </Box>
    );
};
